import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Home,
  Add,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn,
  ToggleOff,
  Settings,
  ContentCopy
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser } from '../../../hooks/useApi';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WeightingProfileDialog } from '../../../components/admin/WeightingProfileDialog';

interface WeightingProfile {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const WeightingProfiles: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  // State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<WeightingProfile | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<WeightingProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch weighting profiles
  const { data: profilesData, isLoading: profilesLoading } = useQuery({
    queryKey: ['weighting-profiles-all'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/marketcompass/admin/weighting-profiles-list');
      if (!response || !response.ok) throw new Error('Failed to fetch weighting profiles');
      return response.json();
    },
    enabled: !!user
  });

  const profiles: WeightingProfile[] = profilesData?.data || [];

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async (profileId: number) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/weighting-profiles/${profileId}/toggle-active`,
        { method: 'PUT' }
      );
      if (!response) {
        throw new Error('No response from server');
      }
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle active status');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weighting-profiles-all'] });
      setSuccess('Active status updated successfully');
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (profileId: number) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/weighting-profiles/${profileId}/delete`,
        { method: 'DELETE' }
      );
      if (!response) {
        throw new Error('No response from server');
      }
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete profile');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weighting-profiles-all'] });
      setSuccess('Profile deleted successfully');
      setDeleteDialogOpen(false);
      setProfileToDelete(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  const handleAddNew = () => {
    setEditingProfile(null);
    setDialogOpen(true);
  };

  const handleEdit = (profile: WeightingProfile) => {
    setEditingProfile(profile);
    setDialogOpen(true);
  };

  const handleClone = async (profile: WeightingProfile) => {
    try {
      // Fetch full profile data with all weights
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/weighting-profiles/${profile.id}`
      );

      if (!response) {
        setError('Failed to fetch profile for cloning');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch profile for cloning');
        return;
      }

      const result = await response.json();
      const fullProfile = result.data;

      // Create a new profile object without ID and with modified name
      const clonedProfile = {
        ...fullProfile,
        id: undefined as any, // Remove ID so dialog treats it as new
        name: `${fullProfile.name} (Copy)`,
        created_at: undefined as any,
        updated_at: undefined as any
      };

      setEditingProfile(clonedProfile);
      setDialogOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clone profile');
    }
  };

  const handleDelete = (profile: WeightingProfile) => {
    setProfileToDelete(profile);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (profileToDelete) {
      deleteMutation.mutate(profileToDelete.id);
    }
  };

  const handleToggleActive = (profileId: number) => {
    toggleActiveMutation.mutate(profileId);
  };

  const handleDialogClose = (success?: boolean) => {
    setDialogOpen(false);
    setEditingProfile(null);
    if (success) {
      queryClient.invalidateQueries({ queryKey: ['weighting-profiles-all'] });
      setSuccess(editingProfile ? 'Profile updated successfully' : 'Profile created successfully');
    }
  };

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 300
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 180,
      valueFormatter: (value: string) => {
        return new Date(value).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row as WeightingProfile)}
            title="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleClone(params.row as WeightingProfile)}
            title="Clone"
          >
            <ContentCopy fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color={params.row.is_active ? 'warning' : 'success'}
            onClick={() => handleToggleActive(params.row.id)}
            title={params.row.is_active ? 'Deactivate' : 'Activate'}
          >
            {params.row.is_active ? (
              <ToggleOff fontSize="small" />
            ) : (
              <ToggleOn fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row as WeightingProfile)}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/market-compass/market-rankings');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Market Rankings
          </Link>
          <Typography color="text.primary">Weighting Profiles</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Settings sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                Weighting Profiles
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Manage weighting profiles for market ranking calculations
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            size="large"
          >
            Add New Profile
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Data Grid */}
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={profiles}
            columns={columns}
            loading={profilesLoading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } }
            }}
            disableRowSelectionOnClick
          />
        </Paper>

        {/* Add/Edit Dialog */}
        <WeightingProfileDialog
          open={dialogOpen}
          profile={editingProfile}
          onClose={handleDialogClose}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the profile "{profileToDelete?.name}"?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
